import minBy from 'lodash/minBy';
import type { ILine } from '../models/line.model';
import { TransportMode } from '../models/transport-mode.enum';

export class LineHelper {
  public static getLineNameAndDirection(line: ILine): string {
    if (!line) {
      return '';
    }
    return `${line.name ? line.name : line.shortName} ${line.directionName}`;
  }

  public static areLinesPracticallyTheSame(line1: ILine, line2: ILine): boolean {
    return line1.gid === line2.gid && line1.directionName === line2.directionName && line1.foregroundColor === line2.foregroundColor && line1.backgroundColor === line2.backgroundColor;
  }

  public static getAppliesFromText(line: ILine, translationFrom: string) {
    return `${translationFrom.toLocaleLowerCase()} ${line.appliesFrom?.toFormat('yyyy-MM-dd') ?? ''}`;
  }

  // Read NV-2749 for more background on this.
  // Lines are versioned by the composite key of gid + number, and they have a validity date interval.
  // For travel planning we display future line versions that are not yet valid, such that users can plan their trip ahead of time.
  // Sometimes only trivial fields, like contractorId, change from one version to another.
  // For end users, these are practically duplicates, so we only display the currently valid one.
  private static getRelevantLine?(lines: ILine[], line: ILine): ILine | undefined {
    const practicallyTheSameLines = lines.filter((l) => this.areLinesPracticallyTheSame(l, line));

    if (practicallyTheSameLines.length === 0) {
      return undefined;
    }

    if (practicallyTheSameLines.length === 1) {
      return line;
    }

    const earliestLine = minBy(practicallyTheSameLines, 'appliesFrom');
    if (earliestLine === undefined) {
      return undefined;
    }

    const earliestLineAppliesFrom = earliestLine.appliesFrom;

    if (line.appliesFrom === earliestLineAppliesFrom) {
      return line;
    }

    return undefined;
  }

  public static getRelevantLines(lines: ILine[]): ILine[] {
    const distinctLines: ILine[] = [];

    lines.forEach((line) => {
      const distinctLine = this.getRelevantLine ? this.getRelevantLine(lines, line) : undefined;

      if (distinctLine) {
        distinctLines.push(distinctLine);
      }
    });

    return distinctLines;
  }

  public static getBackgroundColor(line: ILine): string {
    if (line && line.backgroundColor) {
      if (line.backgroundColor[0] === '#') {
        return line.backgroundColor;
      }
      return `#${line.backgroundColor}`;
    }
    return '#006C93';
  }

  public static getForegroundColor(line: ILine): string {
    if (line && line.foregroundColor) {
      if (line.foregroundColor[0] === '#') {
        return line.foregroundColor;
      }
      return `#${line.foregroundColor}`;
    }
    return '#FFFFFF';
  }

  public static getBorderColor(line: ILine): string {
    if (line && line.borderColor) {
      if (line.borderColor[0] === '#') {
        return line.borderColor;
      }
      return `#${line.borderColor}`;
    }
    return LineHelper.getBackgroundColor(line);
  }

  public static getBackgroundSafeForegroundColor(line: ILine | undefined): string | undefined {
    const foregroundColor = line?.foregroundColor;
    if (LineHelper.isBackgroundSafeForegroundColor(foregroundColor, line)) {
      return foregroundColor;
    }
    return line?.backgroundColor;
  }

  public static getBackgroundSafeBackgroundColor(line: ILine | undefined): string | undefined {
    const foregroundColor = line?.foregroundColor;
    if (LineHelper.isBackgroundSafeForegroundColor(foregroundColor, line)) {
      return line?.backgroundColor;
    }
    return foregroundColor;
  }

  private static isBackgroundSafeForegroundColor(foregroundColor: string | undefined, line: ILine | undefined): boolean {
    if (!foregroundColor || !line) return true;
    return foregroundColor !== '#ffffff' && foregroundColor !== '#ffff50' && line?.transportMode !== TransportMode.bussondemand;
  }
}
